'use client';

import { useState, useEffect, useCallback } from 'react';
import Toast from '@alltrails/denali/components/Toast';
import CheckCircle from '@alltrails/denali/icons/CheckCircle';
import Error from '@alltrails/denali/icons/Error';
import logError from '@alltrails/core/utils/logError';
import { destroyFlashMessage } from '../../utils/destroyFlashMessage';

export type FlashMessageType = 'alert' | 'warning' | 'error' | 'notice' | 'gift';

export type FlashMessage = {
  type: FlashMessageType;
  message: string;
  id: string;
};

type FlashMessagesProps = {
  flashes: FlashMessage[];
};

type ToastType = 'error' | 'neutral';

const typeMap: Record<FlashMessageType, { type: ToastType; icon: { Component: typeof Error | typeof CheckCircle } }> = {
  alert: { type: 'error', icon: { Component: Error } },
  warning: { type: 'error', icon: { Component: Error } },
  error: { type: 'error', icon: { Component: Error } },
  notice: { type: 'neutral', icon: { Component: CheckCircle } },
  gift: { type: 'neutral', icon: { Component: CheckCircle } }
};

const FlashMessages = ({ flashes }: FlashMessagesProps) => {
  const [flashMessages, setFlashMessages] = useState<FlashMessage[]>(flashes);
  const [currentFlash, setCurrentFlash] = useState<FlashMessage | null>(flashMessages[0] ?? null);
  const [isOpen, setIsOpen] = useState(!!currentFlash);

  const removeFlashMessage = useCallback(
    async (id: string) => {
      try {
        await destroyFlashMessage(id);
      } catch (error) {
        logError(error as any);
      }
      setFlashMessages(prevMessages => prevMessages.filter(message => message.id !== id));
    },
    []
  );

  const handleCloseComplete = useCallback(() => {
    if (currentFlash) {
      removeFlashMessage(currentFlash.id);
    }
  }, [currentFlash, removeFlashMessage]);

  // Update current flash when flash messages change
  useEffect(() => {
    const nextFlash = flashMessages[0] ?? null;
    setCurrentFlash(nextFlash);
    setIsOpen(!!nextFlash);
  }, [flashMessages]);

  if (!currentFlash) {
    return null;
  }

  const config = typeMap[currentFlash.type];

  return (
    <Toast
      icon={config?.icon}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      onCloseComplete={handleCloseComplete}
      message={currentFlash.message}
      type={config?.type ?? 'neutral'}
      testId={currentFlash.id}
    />
  );
};

export default FlashMessages;

